/**
 * The fill_in_blanks codec: inline authored text ⇄ stored `{ text, blanks }`.
 *
 * Stored `text` marks gaps with a bare `{{}}` and keeps the answers in a parallel `blanks` array,
 * matched by index — the shape the player grades. That shape is unauthorable: nothing on screen tells
 * you which gap `blanks[2]` answers, which is why the old editor needed a counter, a mismatch warning
 * and move buttons to prop it up.
 *
 * So the author writes the answer inside the braces instead — `7 + {{five}} = {{twelve}}` — and this
 * codec derives the stored pair. The mapping problem stops existing: a gap and its answer are the
 * same characters. No schema bump and no player change; this is purely app-admin's editing
 * representation.
 *
 * ONE ANSWER PER GAP. `Blank.accepted` stays an array for the player, but the admin authors exactly
 * one answer, so `fromInlineText` always writes a single-element array; a stored blank with several
 * is read as its first and the rest are dropped on the next save.
 *
 * WHY `{{…}}` AND NOT `[[…]]` — asked and rejected 2026-08-17. `[[…]]` is the TOKEN syntax
 * (`[[kid_name]]`), and `fillTokens` resolves tokens in the fill-in-blanks sentence specifically, so
 * under `[[…]]` a greeting would silently parse as a gap answered `kid_name`. The motivation —
 * freeing an answer to contain braces for a KaTeX `\frac{1}{2}` — is better served, when it is
 * actually needed, by a balanced-brace scanner here than by a new delimiter or a change to the stored
 * form.
 *
 * THE RULE THAT MAKES THIS SAFE: `toInlineText(fromInlineText(s)) === s` must hold for EVERY string,
 * byte for byte. The textarea is recomputed from the store on every keystroke, so a round trip that
 * "tidies" its input rewrites the text under the author's caret mid-word. Answers are captured
 * VERBATIM — never trimmed, cased, collapsed or reordered here; trimming is the grader's job. Add
 * nothing to this file that reformats.
 */
import type { Blank } from '../../model/assessments/interfaces'

import { GAP_MARKER, GAP_TOKEN_REGEX } from '../assessments/schema'

/** The stored pair. Both fields always move together — a text without its blanks is the
 *  mismatch state refinement 2 treats as a bug. */
interface InlineGapsResult {
  text: string
  blanks: Blank[]
}

/** An answer containing a brace cannot round-trip through `{{…}}` (see `GAP_TOKEN_REGEX`), so it is
 *  shown as an empty gap rather than a token that would re-parse into something else. Only reachable
 *  from legacy stored data — nothing writes such an answer. */
const isInlineable = (answer: string): boolean => !answer.includes('{') && !answer.includes('}')

/**
 * Stored `{ text, blanks }` → the inline string the author edits. Gaps are matched to blanks
 * POSITIONALLY, which is the stored contract. A gap with no corresponding blank (or an unusable
 * answer) renders as a bare `{{}}`, which refinement 2 reports by position on save.
 */
export const toInlineText = (text: string, blanks: Blank[]): string => {
  let gapIndex = -1

  return text.replace(GAP_TOKEN_REGEX, () => {
    gapIndex += 1
    const answer = blanks[gapIndex]?.accepted?.[0] ?? ''

    return isInlineable(answer) ? `{{${answer}}}` : GAP_MARKER
  })
}

/**
 * The inline string the author edits → stored `{ text, blanks }`. Every `{{…}}` becomes one gap and
 * one blank, in order; anything that is not a complete token — a half-typed `{{`, a stray `}}` — is
 * left alone as literal text, so the document stays coherent while the author is mid-keystroke.
 */
export const fromInlineText = (inline: string): InlineGapsResult => {
  const blanks: Blank[] = []

  const text = inline.replace(GAP_TOKEN_REGEX, (_match: string, answer: string) => {
    blanks.push({ accepted: [answer] })

    return GAP_MARKER
  })

  return { text, blanks }
}
